import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import * as d3 from 'd3';
import html2canvas from 'html2canvas';
import { RefreshCw, Download, Network, Grid3x3 } from 'lucide-react';
import QuadrantGridView from '../components/QuadrantGridView';
import useViewportSize from '../hooks/useViewportSize';
import { useScreenOrientation } from '../hooks/useScreenOrientation';
import LandscapePrompt from '../components/LandscapePrompt';
import { auth } from '../auth';
import './StakeholderMap.css';

// Types
interface Node {
  id: string;
  name: string;
  type: 'client' | 'stakeholder';
  relationship?: string;
  influence_level?: number;
  interest_level?: number;
  x?: number;
  y?: number;
  fx?: number | null;
  fy?: number | null;
}

interface Edge {
  id: string;
  source: string | Node;
  target: string | Node;
  type: string;
  relationship_type?: string;
  strength?: number;
}

interface NetworkData {
  success: boolean;
  nodes: Node[];
  edges: Edge[];
}

// Helper functions
const canonRelWide = (raw: any): 'supportive' | 'neutral' | 'opposed' | 'unknown' => {
  const s = String(raw ?? '').trim().toLowerCase();
  if (!s) return 'unknown';
  if (['supportive', 'support', 'ally', 'advocate', 'supporter', 'for', 'pro', 'positive', 'favorable', 'favourable', 'backing'].includes(s)) 
    return 'supportive';
  if (['neutral', 'mixed', 'ambivalent'].includes(s)) 
    return 'neutral';
  if (['opposed', 'oppose', 'opponent', 'against', 'anti', 'adversarial', 'hostile', 'negative', 'unfavorable', 'unfavourable', 'blocking'].includes(s)) 
    return 'opposed';
  return 'unknown';
};

const relationshipColor = (rel: string): string => {
  const colors = {
    supportive: '#1E8E3E',
    neutral: '#F59E0B',  // Amber/Yellow (matches Insights page)
    opposed: '#D93025',
    unknown: '#9CA3AF'
  };
  return colors[canonRelWide(rel) as keyof typeof colors] || colors.unknown;
};

const edgeColor = (type: string): string => {
  const t = String(type || '').toLowerCase();
  if (t.includes('support') || t.includes('ally')) return '#1E8E3E';
  if (t.includes('oppos') || t.includes('against')) return '#D93025';
  if (t.includes('influenc')) return '#3B82F6';
  return '#9AA0A6';
};

const edgeStyle = (type: string): string => {
  const t = String(type || '').toLowerCase();
  if (t.includes('passive')) return '5,5';
  if (t.includes('indirect')) return '2,2';
  return '';
};

const nodeSize = (influenceLevel: any): number => {
  const n = Number(influenceLevel);
  if (!Number.isNaN(n)) {
    if (n >= 5) return 16;
    if (n >= 3) return 12;
    return 8;
  }
  return 12;
};

const StakeholderMap: React.FC = () => {
  // All hooks must be at the top
  const navigate = useNavigate();
  const user = auth.getUser();
  const orientation = useScreenOrientation();
  const [view, setView] = useState<'network' | 'grid'>('network');
  const [networkData, setNetworkData] = useState<NetworkData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEdges, setShowEdges] = useState(true);
  const [influenceFilter, setInfluenceFilter] = useState<string>('all');
  const [relationshipFilter, setRelationshipFilter] = useState<string>('all');
  const [layoutMode, setLayoutMode] = useState<'force' | 'relationship' | 'influence' | 'grid'>('force');
  
  const svgRef = useRef<SVGSVGElement>(null);
  const simulationRef = useRef<d3.Simulation<Node, Edge> | null>(null);
  const viewport = useViewportSize();

  // Responsive dimensions
  const width = Math.max(800, viewport.w - 100);
  const height = Math.max(600, Math.min(viewport.h - 300, 900));

  // Fetch network data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const clientId = auth.getClientId();
        if (!clientId) {
          throw new Error('No client ID found. Please log in again.');
        }

        const response = await fetch(`/api/relationships/network/${clientId}?cb=${Date.now()}`, {
          cache: 'no-store',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch network data: ${response.statusText}`);
        }

        const data = await response.json();
        setNetworkData(data);
      } catch (err) {
        console.error('Error fetching stakeholder map:', err);
        setError(err instanceof Error ? err.message : 'Failed to load stakeholder map');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter nodes
  const filteredNodes = useMemo(() => {
    if (!networkData) return [];
    
    return networkData.nodes.filter(node => {
      // Influence filter
      if (influenceFilter !== 'all') {
        const level = Number(node.influence_level || 0);
        if (influenceFilter === 'high' && level < 5) return false;
        if (influenceFilter === 'medium' && (level < 3 || level >= 5)) return false;
        if (influenceFilter === 'low' && level >= 3) return false;
      }
      
      // Relationship filter
      if (relationshipFilter !== 'all') {
        const rel = canonRelWide(node.relationship);
        if (rel !== relationshipFilter) return false;
      }
      
      return true;
    });
  }, [networkData, influenceFilter, relationshipFilter]);

  // Filter edges
  const filteredEdges = useMemo(() => {
    if (!networkData || !showEdges) return [];
    
    const nodeIds = new Set(filteredNodes.map(n => n.id));
    return networkData.edges.filter(edge => {
      const sourceId = typeof edge.source === 'string' ? edge.source : edge.source.id;
      const targetId = typeof edge.target === 'string' ? edge.target : edge.target.id;
      return nodeIds.has(sourceId) && nodeIds.has(targetId);
    });
  }, [networkData, filteredNodes, showEdges]);

  // D3 Force Simulation
  useEffect(() => {
    if (view !== 'network' || !svgRef.current || filteredNodes.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const g = svg.append('g');

    // Initialize nodes with centered positions to avoid off-screen rendering
    filteredNodes.forEach(node => {
      if (node.x === undefined) node.x = width / 2 + (Math.random() - 0.5) * 100;
      if (node.y === undefined) node.y = height / 2 + (Math.random() - 0.5) * 100;
    });

    // Zoom behavior
    const zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 4])
      .on('zoom', (event) => {
        g.attr('transform', event.transform);
      });

    svg.call(zoom);
    
    // Set initial zoom to center the view
    const initialTransform = d3.zoomIdentity.translate(0, 0).scale(1);
    svg.call(zoom.transform, initialTransform);

    // Create simulation
    const simulation = d3.forceSimulation<Node>(filteredNodes)
      .force('link', d3.forceLink<Node, Edge>(filteredEdges)
        .id(d => d.id)
        .distance(100))
      .force('charge', d3.forceManyBody().strength(-300))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(30));

    // Apply layout mode
    if (layoutMode === 'relationship') {
      simulation.force('x', d3.forceX<Node>(d => {
        const rel = canonRelWide(d.relationship);
        if (rel === 'supportive') return width * 0.75;
        if (rel === 'neutral') return width * 0.5;
        if (rel === 'opposed') return width * 0.25;
        return width * 0.9;
      }).strength(0.5));
    } else if (layoutMode === 'influence') {
      simulation.force('y', d3.forceY<Node>(d => {
        const level = Number(d.influence_level || 0);
        if (level >= 5) return height * 0.25;
        if (level >= 3) return height * 0.5;
        return height * 0.75;
      }).strength(0.5));
    } else if (layoutMode === 'grid') {
      simulation.force('x', d3.forceX<Node>(d => {
        const rel = canonRelWide(d.relationship);
        if (rel === 'supportive') return width * 0.75;
        if (rel === 'neutral') return width * 0.5;
        if (rel === 'opposed') return width * 0.25;
        return width * 0.9;
      }).strength(0.8));
      simulation.force('y', d3.forceY<Node>(d => {
        const level = Number(d.influence_level || 0);
        if (level >= 5) return height * 0.25;
        if (level >= 3) return height * 0.5;
        return height * 0.75;
      }).strength(0.8));
    }

    // Draw edges
    const link = g.append('g')
      .selectAll('line')
      .data(filteredEdges)
      .join('line')
      .attr('stroke', d => edgeColor(d.relationship_type || d.type))
      .attr('stroke-width', d => Math.max(1, (d.strength || 1) * 0.5))
      .attr('stroke-dasharray', d => edgeStyle(d.relationship_type || d.type))
      .attr('opacity', 0.6);

    // Draw nodes
    const node = g.append('g')
      .selectAll('circle')
      .data(filteredNodes)
      .join('circle')
      .attr('r', d => nodeSize(d.influence_level))
      .attr('fill', d => d.type === 'client' ? '#3B82F6' : relationshipColor(d.relationship || ''))
      .attr('stroke', '#fff')
      .attr('stroke-width', 2)
      .style('cursor', 'pointer')
      .on('click', (event, d) => {
        // Navigate to stakeholder detail page (skip client nodes)
        if (d.type === 'stakeholder' && d.id) {
          navigate(`/stakeholders/${d.id}`);
        }
      })
      .call(d3.drag<any, Node>()
        .on('start', (event, d) => {
          if (!event.active) simulation.alphaTarget(0.3).restart();
          d.fx = d.x;
          d.fy = d.y;
        })
        .on('drag', (event, d) => {
          d.fx = event.x;
          d.fy = event.y;
        })
        .on('end', (event, d) => {
          if (!event.active) simulation.alphaTarget(0);
          d.fx = null;
          d.fy = null;
        }) as any);

    // Add tooltips
    node.append('title')
      .text(d => `${d.name}\nType: ${d.type}\nRelationship: ${canonRelWide(d.relationship)}\nInfluence: ${d.influence_level || 'N/A'}`);

    // Add labels
    const label = g.append('g')
      .selectAll('text')
      .data(filteredNodes)
      .join('text')
      .text(d => d.name.length > 20 ? d.name.substring(0, 18) + '...' : d.name)
      .attr('font-size', 10)
      .attr('dx', 15)
      .attr('dy', 4)
      .attr('fill', '#374151')
      .style('pointer-events', 'none');

    // Update positions on tick
    simulation.on('tick', () => {
      link
        .attr('x1', d => (d.source as Node).x || 0)
        .attr('y1', d => (d.source as Node).y || 0)
        .attr('x2', d => (d.target as Node).x || 0)
        .attr('y2', d => (d.target as Node).y || 0);

      node
        .attr('cx', d => d.x || 0)
        .attr('cy', d => d.y || 0);

      label
        .attr('x', d => d.x || 0)
        .attr('y', d => d.y || 0);
    });

    simulationRef.current = simulation;

    return () => {
      simulation.stop();
    };
  }, [view, filteredNodes, filteredEdges, width, height, layoutMode]);

  // Export as PNG
  const exportPNG = async () => {
    const container = document.querySelector('.stakeholder-map-container');
    if (!container) return;

    try {
      const canvas = await html2canvas(container as HTMLElement);
      const link = document.createElement('a');
      link.download = `stakeholder-map-${new Date().toISOString().split('T')[0]}.png`;
      link.href = canvas.toDataURL();
      link.click();
    } catch (err) {
      console.error('Error exporting PNG:', err);
      alert('Failed to export image');
    }
  };

  const refresh = () => {
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="stakeholder-map-loading" data-tour="stakeholder-map-canvas">
        <div className="spinner"></div>
        <p>Loading stakeholder map...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="stakeholder-map-error" data-tour="stakeholder-map-canvas">
        <h3>Error Loading Stakeholder Map</h3>
        <p>{error}</p>
        <button onClick={refresh} className="btn-primary">Retry</button>
      </div>
    );
  }

  const handleLogout = () => {
    auth.logout();
    navigate('/login');
  };

  return (
    <>
      {/* Top Navigation Bar */}
      <div className="stakeholder-map-topnav">
        <div className="topnav-left">
          <img src="/heat-logo.png" alt="HEAT" className="topnav-logo" />
          <span className="topnav-client">{user?.client?.name || 'Client Portal'}</span>
        </div>
        <div className="topnav-right">
          <button onClick={() => navigate('/dashboard')} className="topnav-back-btn">
            ← Back to Dashboard
          </button>
          <span className="topnav-user">{user?.fullName}</span>
          <button onClick={handleLogout} className="topnav-logout-btn">
            Logout
          </button>
        </div>
      </div>

      <div className="stakeholder-map-container">
      {/* Landscape orientation prompt for mobile users in portrait mode */}
      {orientation === 'portrait' && viewport.w < 1024 && (
        <LandscapePrompt pageName="stakeholder-map" />
      )}

      <div className="stakeholder-map-header">
        <h1>Stakeholder Map</h1>
        <div className="header-actions">
          <button onClick={refresh} className="btn-secondary">
            <RefreshCw size={16} style={{ marginRight: '6px' }} /> Refresh
          </button>
          <button onClick={exportPNG} className="btn-primary">
            <Download size={16} style={{ marginRight: '6px' }} /> Download PNG
          </button>
        </div>
      </div>

      <div className="stakeholder-map-controls">
        <div className="control-group">
          <label>View:</label>
          <div className="btn-group">
            <button
              className={view === 'network' ? 'active' : ''}
              onClick={() => setView('network')}
            >
              <Network size={16} style={{ marginRight: '6px' }} /> Network View
            </button>
            <button
              className={view === 'grid' ? 'active' : ''}
              onClick={() => setView('grid')}
            >
              <Grid3x3 size={16} style={{ marginRight: '6px' }} /> Grid View
            </button>
          </div>
        </div>

        {view === 'network' && (
          <>
            <div className="control-group">
              <label>Layout:</label>
              <select value={layoutMode} onChange={(e) => setLayoutMode(e.target.value as any)}>
                <option value="force">Natural (Force)</option>
                <option value="relationship">By Relationship</option>
                <option value="influence">By Influence</option>
                <option value="grid">Grid Layout</option>
              </select>
            </div>

            <div className="control-group">
              <label>
                <input
                  type="checkbox"
                  checked={showEdges}
                  onChange={(e) => setShowEdges(e.target.checked)}
                />
                Show Relationship Edges
              </label>
            </div>
          </>
        )}

        <div className="control-group">
          <label>Influence Level:</label>
          <select value={influenceFilter} onChange={(e) => setInfluenceFilter(e.target.value)}>
            <option value="all">All Levels</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>

        <div className="control-group">
          <label>Relationship:</label>
          <select value={relationshipFilter} onChange={(e) => setRelationshipFilter(e.target.value)}>
            <option value="all">All Relationships</option>
            <option value="supportive">Supportive</option>
            <option value="neutral">Neutral</option>
            <option value="opposed">Opposed</option>
            <option value="unknown">Unknown</option>
          </select>
        </div>
      </div>

      <div className="stakeholder-map-content" data-tour="stakeholder-map-canvas">
        {view === 'network' ? (
          <svg
            ref={svgRef}
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            className="network-svg"
          />
        ) : (
          <QuadrantGridView
            stakeholders={filteredNodes.filter(n => n.type === 'stakeholder')}
            width={width}
            height={height}
          />
        )}
      </div>

      <div className="stakeholder-map-legend">
        <h4>Legend</h4>
        <div className="legend-items">
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: '#1E8E3E' }}></span>
            <span>Supportive</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: '#F59E0B' }}></span>
            <span>Neutral</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: '#D93025' }}></span>
            <span>Opposed</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: '#9CA3AF' }}></span>
            <span>Unknown</span>
          </div>
          <div className="legend-item">
            <span className="legend-color" style={{ backgroundColor: '#3B82F6' }}></span>
            <span>Client</span>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default StakeholderMap;
